import React, { useEffect, useState } from "react";

// City hotspot coordinates in PERCENTAGES of the reference artwork
// (/images/coverage-map.jpg — 1536×1024, 3:2). These were measured directly
// from the artwork's own gold dots via automated pixel detection so the
// animated SVG layer overlays 1:1 on top of the printed dots.
//
// Do NOT eyeball-adjust these values — the PNG is the geographical source
// of truth. Vaduz (Liechtenstein) is the only synthetic node, placed on the
// Swiss silhouette between Zürich and Graubünden per client brief.
const CITIES = [
  // Central hub
  { k: "Zürich",       x: 53.96, y: 34.45, hub: true },
  // Swiss network
  { k: "Basel",        x: 39.41, y: 29.18 },
  { k: "Bern",         x: 42.57, y: 46.25 },
  { k: "Lucern",       x: 53.93, y: 42.78 },
  { k: "Interlaken",   x: 48.20, y: 47.00, silent: true }, // no dot on PNG — silent waypoint
  { k: "Grindelwald",  x: 55.58, y: 54.78 },
  { k: "Genève",       x: 36.24, y: 56.05 },
  { k: "Graubünden",   x: 69.16, y: 47.90 },
  { k: "St. Moritz",   x: 73.91, y: 52.83 },
  { k: "Locarno",      x: 59.90, y: 62.76 },
  { k: "Lugano",       x: 62.03, y: 67.42 },
  { k: "Vaduz",        x: 64.50, y: 43.90, synthetic: true }, // Liechtenstein
  // Neighbours
  { k: "Como",         x: 62.61, y: 72.32 },
  { k: "Milano",       x: 64.21, y: 79.49 },
  { k: "Paris",        x: 12.03, y:  9.26 },
  { k: "Strasbourg",   x: 37.72, y: 10.52 },
  { k: "Freiburg",     x: 41.79, y: 16.45 },
  { k: "München",      x: 76.47, y: 20.56 },
  { k: "Lyon",         x: 12.79, y: 52.72 },
];

// Route segments — travelling signal packets move FROM the first city TO
// the second. Liechtenstein wired between Zürich and Graubünden.
const ROUTES = [
  ["Zürich", "Basel"],
  ["Zürich", "Lucern"],
  ["Zürich", "Bern"],
  ["Zürich", "Grindelwald"],
  ["Zürich", "München"],
  ["Zürich", "Graubünden"],
  ["Zürich", "Genève"],
  ["Zürich", "Vaduz"],
  ["Vaduz", "Graubünden"],
  ["Basel", "Strasbourg"],
  ["Basel", "Paris"],
  ["Lucern", "Interlaken"],
  ["Interlaken", "Grindelwald"],
  ["Bern", "Genève"],
  ["Graubünden", "St. Moritz"],
  ["Grindelwald", "Locarno"],
  ["Locarno", "Lugano"],
  ["Lugano", "Como"],
  ["Como", "Milano"],
  ["Genève", "Lyon"],
];

const at = (k) => CITIES.find((c) => c.k === k);

/**
 * Live SwissAlpenLine coverage map.
 *
 * The static PNG (`/images/coverage-map.jpg`) is the geographical source of
 * truth. We overlay a full-bleed SVG that renders:
 *   • pulsing halos precisely on top of every printed city dot
 *   • travelling gold "signal packets" gliding along every route line
 *   • a synthetic Vaduz node (Liechtenstein) — no dot exists on the PNG
 *
 * No borders, frames or margins wrap the artwork — the background must flow
 * seamlessly into the page (client brief).
 */
export const CoverageMap = () => {
  const [visible, setVisible] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.2 }
    );
    io.observe(wrapRef.current);
    return () => io.disconnect();
  }, []);

  const gold = "#B08D2F";

  return (
    <div
      ref={wrapRef}
      data-testid="coverage-map"
      className="relative w-full"
      style={{ aspectRatio: "3 / 2" }}
    >
      {/* Base artwork — full bleed, no frame */}
      <img
        src="/images/coverage-map.jpg"
        alt="SwissAlpenLine coverage — Switzerland and neighbouring cities"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1600ms] ease-out ${
          visible ? "opacity-100 scale-100" : "opacity-0 scale-[1.02]"
        }`}
      />

      {/* Interactive layer — city halos + travelling signals */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      >
        {/* Route lines + travelling signal packets */}
        {visible && ROUTES.map(([a, b], i) => {
          const A = at(a), B = at(b);
          if (!A || !B) return null;
          const dur = 3.4 + (i % 4) * 0.35;
          const delay = 0.6 + (i * 0.22);
          return (
            <g key={`sig-${a}-${b}`}>
              <line
                x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                stroke={gold} strokeOpacity="0.22" strokeWidth="0.18"
              />
              <circle r="0.55" fill={gold}>
                <animate
                  attributeName="cx"
                  values={`${A.x};${B.x};${B.x}`}
                  keyTimes="0;0.7;1"
                  dur={`${dur}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="cy"
                  values={`${A.y};${B.y};${B.y}`}
                  keyTimes="0;0.7;1"
                  dur={`${dur}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.15;0.7;1"
                  dur={`${dur}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          );
        })}

        {/* City halos — light gold rings pulse exactly on top of PNG dots */}
        {visible && CITIES.map((c, idx) => {
          // Silent waypoints (e.g. Interlaken has no printed dot) don't get an
          // extra filled dot — only a faint halo — to avoid ghost markers.
          const holeSize = c.hub ? 1.0 : c.silent ? 0 : 0.55;
          const haloBase = c.hub ? 0.8 : 0.5;
          const haloMax  = c.hub ? 3.8 : c.silent ? 1.8 : 2.4;
          return (
            <g key={`halo-${c.k}`}>
              {holeSize > 0 && (
                <circle
                  cx={c.x} cy={c.y}
                  r={holeSize}
                  fill={gold}
                  opacity={c.hub ? 0.9 : c.synthetic ? 0.75 : 0.55}
                />
              )}
              <circle
                cx={c.x} cy={c.y}
                r="0"
                fill="none"
                stroke={gold}
                strokeOpacity={c.hub ? 0.65 : 0.35}
                strokeWidth={c.hub ? 0.25 : 0.15}
              >
                <animate
                  attributeName="r"
                  values={`${haloBase};${haloMax};${haloBase}`}
                  dur={c.hub ? "2.4s" : "3.6s"}
                  begin={`${((idx * 0.17) % 1.6).toFixed(2)}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  values={c.hub ? "0.75;0;0.75" : "0.5;0;0.5"}
                  dur={c.hub ? "2.4s" : "3.6s"}
                  begin={`${((idx * 0.17) % 1.6).toFixed(2)}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default CoverageMap;
